import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {IState} from '../../../shared/store/reducers';
import {useCurrentUser} from '../../../shared/hooks';
import {isAdmin, isLoggedIn} from '../../../shared/utils/auth';
import {setReadOnly} from '../../../shared/store/actions/reportFormActions';
import {IRole, ISharedWithGroup, ISharedWithUser, IUser} from '../../../shared/models';

export function useSetReadOnly(uid: string): void {
  /*
   * Check user permission in order to determine if it can edit the report or not
   */
  const sharingSettings = useSelector((state: IState) => state.sharingSettings.reportSharingSettings);
  const currentUser: IUser = useCurrentUser() as IUser;
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoggedIn(currentUser)) {
      return;
    } else if (isAdmin(currentUser)) {
      // admins can edit any report
      dispatch(setReadOnly(false));
      return;
    }

    if(!sharingSettings) {
      return;
    }
    if (sharingSettings.shared_with_workspace.edit) {
      // report is shared with workspace, so it can be edited by every user
      dispatch(setReadOnly(false));
    } else if (sharingSettings.shared_with_groups.length > 0) {
      /*
       * 1. Find if the current user's group is in the group list.
       * 2. Find out if the group has `edit` permission.
       */
      let found = false;

      const roles = currentUser.roles?.map((role: IRole) => role.uid) || [];
      sharingSettings.shared_with_groups.forEach((group: ISharedWithGroup) => {
        if (found) {
          // ignore rest of the groups if a group that the user belongs to has edit permissions.
          return;
        }
        if (roles.includes(group.uid) && group.edit) {
          found = true;
          dispatch(setReadOnly(false));
        }
      });
    } else if (sharingSettings.shared_with_users.length > 0) {
      // find if the current user can edit the report
      const found = sharingSettings.shared_with_users.find((s: ISharedWithUser) => s.uid === currentUser.uid);

      if (found && found.edit) {
        dispatch(setReadOnly(false));
      }
    }
  }, [sharingSettings?.uid]);
}

export function useIsReadOnly(): boolean {
  const readOnly: boolean = useSelector((state: IState) => state.reportForm.readOnly);

  return readOnly;
}
